import React, { ReactNode, createContext, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';

//helpers
import { displayNames, getMediaQuery, getElements, isValidViewportChild, sortingLogic } from './helpers';

//types
import {
  ViewportContextType,
  ViewportComponentProps,
  ViewportProps,
  VIEWPORT_SIZES,
} from './types';

// Create a Context to ensure that the viewport is available to all components
const ViewportContext = createContext<ViewportContextType>(false);

// Generic Viewport component
const ViewportComponent: React.FC<ViewportComponentProps> = ({ children, type }) => {
  const isInsideViewport = useContext(ViewportContext);

  if (!isInsideViewport) {
    throw new Error(`${type} must be used within a Viewport component`);
  }

  const mediaQuery = getMediaQuery(type);
  const matches = useMediaQuery(mediaQuery);
  return matches ? <>{children}</> : null;
};

// Parent Viewport component
const Viewport: React.FC<ViewportProps> = ({ children }) => {
  const childrenArray = React.Children.toArray(children);

  // Check if the children are valid Viewport components
  const isValidChildren = childrenArray.every(isValidViewportChild);

  if (!isValidChildren) {
    throw new Error(
      'Viewport children must be valid Viewport components: ViewSmall, ViewMedium, ViewLarge, ViewExtraLarge'
    );
  }

  // Sort the children based on the order of displayNames
  const sortedArray = childrenArray.sort(sortingLogic);

  // Fill the array with missing components
  let lastChild: any = sortedArray[sortedArray.length - 1];
  const filledArray = displayNames
    .map((type, indexI) => {
      const child: any = childrenArray.find((child: any) => child?.type?.displayName === type);
      const indexOfLastChildType = displayNames.findIndex((type) => lastChild?.type?.displayName === type);
      if (child) {
        lastChild = child;
        return child;
      } else {
        if (indexOfLastChildType !== -1 && indexI >= indexOfLastChildType) {
          return React.createElement(getElements(type), lastChild?.props);
        } else {
          return null;
        }
      }
    })
    .filter((child) => child !== null);

  return (
    <ViewportContext.Provider value={true}>
      <>
        {filledArray.map((child: any) => {
          return (
            <ViewportComponent key={child?.type?.displayName} type={child?.type?.displayName}>
              {child}
            </ViewportComponent>
          );
        })}
      </>
    </ViewportContext.Provider>
  );
};

// Subcomponents
const ViewportSmall: React.FC<{ children?: ReactNode }> = (props) => <>{props?.children ? props.children : null}</>;
ViewportSmall.displayName = VIEWPORT_SIZES.sm;

const ViewportMedium: React.FC<{ children?: ReactNode }> = (props) => <>{props?.children ? props.children : null}</>;
ViewportMedium.displayName = VIEWPORT_SIZES.md;

const ViewportLarge: React.FC<{ children?: ReactNode }> = (props) => <>{props?.children ? props.children : null}</>;
ViewportLarge.displayName = VIEWPORT_SIZES.lg;

const ViewportExtraLarge: React.FC<{ children?: ReactNode }> = (props) => (
  <>{props?.children ? props.children : null}</>
);
ViewportExtraLarge.displayName = VIEWPORT_SIZES.xl;

export { Viewport, ViewportSmall, ViewportMedium, ViewportLarge, ViewportExtraLarge };
